import axios from 'axios';
import type { FieldPath, FieldValues, UseFormSetError } from 'react-hook-form';

export interface ValidationErrorResult {
  isValidationError: boolean;
  messages: string[];
}

export const handleValidationErrors = <T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
): ValidationErrorResult => {
  if (!axios.isAxiosError(error) || !error.response?.data) {
    return { isValidationError: false, messages: [] };
  }

  const responseData = error.response.data;

  // Check if it's a validation error response
  if (
    responseData.errorCode === 'VALIDATION' &&
    responseData.errors &&
    typeof responseData.errors === 'object'
  ) {
    const validationErrors = responseData.errors as Record<string, string[]>;
    const allMessages: string[] = [];

    Object.entries(validationErrors).forEach(([fieldPath, messages]) => {
      if (messages && messages.length > 0) {
        const formFieldPath = convertApiFieldPathToFormPath(fieldPath) as FieldPath<T>;

        setError(formFieldPath, {
          type: 'server',
          message: messages[0],
        });

        allMessages.push(...messages);
      }
    });

    // Deduplicate messages to avoid repetition
    const uniqueMessages = [...new Set(allMessages)];

    return { isValidationError: true, messages: uniqueMessages };
  }

  return { isValidationError: false, messages: [] };
};

const convertApiFieldPathToFormPath = (apiFieldPath: string): string => {
  return apiFieldPath
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map((part, index) => {
      if (index === 0) {
        return `model.${part.charAt(0).toLowerCase() + part.slice(1)}`;
      }
      return part.charAt(0).toLowerCase() + part.slice(1);
    })
    .join('.');
};
